import { FixingMode } from "@/data/fixingMode";
import { useNavigate } from "react-router-dom";
import { useTranslation as useTranslationI18n } from "react-i18next";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  getAssetClassTranslation,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { dateSchema } from "@/features/FormCard/utils";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { useTranslation } from "@/hooks/useTranslation";
import i18n from "@/i18n";
import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { fieldTranslations } from "./translations";
import { computeC3FixingMode, requiresC3Fixing, requiresC3MarketValue } from "./utils";
import { FormCleaner } from "@/hooks/useCleanWhenHidden";

const FormSchema = z
  .object({
    date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
    c3_static_datum_id: z.number(),
    c3_static_datum_name: z.preprocess(
      (e: any) => preprocessTextAsyncAutocompleteField(e),
      z.union([
        z.string().nullable().optional(),
        z.object({
          id: z.number(),
          text: z.string(),
          asset_class_1: z.string(),
          asset_class_2: z.string().optional(),
          currency: z.string(),
        }),
      ]),
    ),
    fixing: z.coerce.number().optional(),
    market_value: z.coerce.number().optional(),
    aum: z.preprocess(
      (val) => (val === "" ? null : val),
      z.coerce.number().nullable().optional(),
    ),
    aum_currency: z.string().nullable().optional(),
    currency: z.string(),
  })
  .refine((data) => data.aum == null || !!data.aum_currency, {
    message: "AUM currency is required when AUM is set",
    path: ["aum_currency"],
  })
  .refine(requiresC3MarketValue, {
    message: i18n.t("time_series.errors.market_value_required"),
    path: ["market_value"],
  })
  .refine(requiresC3Fixing, {
    message: i18n.t("time_series.errors.unit_price_required"),
    path: ["fixing"],
  });

const client = Clients.C3;
const table_name = Tables.C3_TIME_SERIES;

export function C3TimeSeriesNew() {
  const { t } = useTranslationI18n();
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({ client, table_name, FormSchema, isNew: true });

  const currencyOptions = useCurrencyOptions();

  const navigateToList = () => navigate(-1);

  const assetData = useWatch({
    control: form.control,
    name: "c3_static_datum_name",
  });

  const { translations } = useTranslation({
    client,
    table_name: Tables.C3_STATIC_DATA,
    language: "fr",
  });

  const fixingMode: FixingMode | null = useMemo(() => {
    if (typeof assetData !== "object") return null;
    return computeC3FixingMode(assetData?.asset_class_1 ?? undefined);
  }, [assetData]);

  useEffect(() => {
    if (assetData && typeof assetData === "object" && assetData.currency) {
      form.setValue(
        "currency",
        currencyOptions.find((opt) => opt.value === assetData.currency)?.label,
      );
    }
  }, [assetData, form, currencyOptions]);

  const cleanSpecs = useMemo(
    () => [
      {
        name: "market_value" as const,
        isVisible: fixingMode !== FixingMode.FIXING,
      },
      {
        name: "fixing" as const,
        isVisible: fixingMode !== FixingMode.VALORISATION,
      },
    ],
    [fixingMode],
  );

  return (
    <div className="bg-gray-50 h-full">
      <div className="container py-6 h-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <FormCleaner
              specs={cleanSpecs}
              ready={!isUpdating}
              keepRegistered={false}
            />
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t("time_series.new_price_valuation")}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name="c3_static_datum_name"
                  label={t(`${table_name}.columns.c3_static_datum_name`, {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}
                  control={form.control}
                  isRequired
                  base_url={`${Clients.C3}/${Tables.C3_STATIC_DATA}`}
                  id_column="c3_static_datum_id"
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: Clients.C3,
                    table_name: Tables.C3_STATIC_DATA,
                    language: "fr",
                  }}
                />
                {assetData && typeof assetData === "object" && (
                  <div className="flex gap-2 text-sm">
                    <div className="flex gap-1">
                      <span className="font-semibold text-gray-700">
                        Classe d'actif:
                      </span>
                      <span className="text-gray-600">
                        {getAssetClassTranslation(
                          assetData.asset_class_1,
                          "asset_class_1",
                          translations,
                        )}
                        {assetData.asset_class_2 &&
                          ` / ${getAssetClassTranslation(
                            assetData.asset_class_2,
                            "asset_class_2",
                            translations,
                          )}`}
                      </span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="aum"
                    label={t(`${table_name}.columns.aum`, {
                      ns: "tables",
                      defaultValue: "AUM",
                    })}
                    control={form.control}
                    type="number"
                  />
                  <AutocompleteLabelField
                    name="aum_currency"
                    label={t(`${table_name}.columns.aum_currency`, {
                      ns: "tables",
                      defaultValue: "AUM Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                  />
                </div>
                <div className={`grid grid-cols-1 gap-4 ${fixingMode === null ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                  {fixingMode !== FixingMode.VALORISATION && (
                    <InputField
                      name="fixing"
                      label={t(`${table_name}.columns.fixing`, {
                        ns: "tables",
                        defaultValue: "Unit price",
                      })}
                      isRequired={fixingMode === FixingMode.FIXING}
                      control={form.control}
                      type="number"
                    />
                  )}
                  {fixingMode !== FixingMode.FIXING && (
                    <InputField
                      name="market_value"
                      label={t(`${table_name}.columns.market_value`, {
                        ns: "tables",
                        defaultValue: "Market value",
                      })}
                      isRequired={fixingMode === FixingMode.VALORISATION}
                      control={form.control}
                      type="number"
                    />
                  )}
                  <AutocompleteLabelField
                    name="currency"
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Currency",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                    fieldOptions={{ isDisabled: true }}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={true}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  ["c3_static_datum_id"],
                  fieldTranslations,
                )
              : t("no_changes")
          }
        />
        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
